// Mode-specific configuration for the TemplateBuilder
// Each mode defines: component palette, canvas dimensions, output renderer

// ─── ADG Brand defaults ───
const BRAND = {
  primaryColor: '#112573',
  accentColor: '#ff8900',
  companyName: 'ADG',
  website: 'www.a-d-g.co.uk',
  phone: '01206 591001',
  email: 'service@a-d-g.co.uk',
};

// ─── FORM MODE (existing) ───
export const FORM_MODE = {
  id: 'form',
  label: 'Form Template',
  canvasSizes: {
    a4: { width: 794, height: 1123, label: 'A4' },
    a5: { width: 559, height: 794, label: 'A5' },
    letter: { width: 816, height: 1056, label: 'US Letter' },
  },
  defaultSize: 'a4',
  components: [
    { id: 'text', label: 'Text', icon: '📝' },
    { id: 'variable', label: 'Variable', icon: '🔤' },
    { id: 'data-table', label: 'Data Table', icon: '📋' },
    { id: 'table', label: 'Table (Manual)', icon: '📊' },
    { id: 'image', label: 'Image/Logo', icon: '🖼️' },
    { id: 'line', label: 'Line', icon: '➖' },
    { id: 'spacer', label: 'Spacer', icon: '⬜' },
  ],
  showAreas: true,
  showVariables: true,
  outputType: 'pdf', // renderTemplateWithData (existing)
};

// ─── PPM QUOTE MODE ───
// Planned Preventive Maintenance quote/renewal templates for the ADG portal.
// Drag-drop PPM-specific elements (asset table, pricing, callout rates, terms,
// acceptance form, feature image) onto an A4 canvas, then export to the SM8
// portal's PPM library via the 📤 Export PPM button.
export const PPM_QUOTE_MODE = {
  id: 'form',  // reuse form output type (absolute-positioned PDF)
  label: 'PPM Quote Template',
  canvasSizes: {
    a4: { width: 794, height: 1123, label: 'A4' },
  },
  defaultSize: 'a4',
  components: [
    // Basics
    { id: 'text', label: 'Text', icon: '📝', defaultW: 400, defaultH: 40 },
    { id: 'heading', label: 'Heading', icon: '📌', defaultW: 400, defaultH: 36 },
    { id: 'variable', label: 'Variable', icon: '🔤', defaultW: 200, defaultH: 24 },
    { id: 'image', label: 'Image/Logo', icon: '🖼️', defaultW: 200, defaultH: 80 },
    { id: 'feature-image', label: 'Feature Image', icon: '🌅', defaultW: 720, defaultH: 240 },
    { id: 'line', label: 'Line', icon: '➖', defaultW: 400, defaultH: 4 },
    { id: 'spacer', label: 'Spacer', icon: '⬜', defaultW: 400, defaultH: 20 },
    { id: 'rectangle', label: 'Rectangle', icon: '▭', defaultW: 200, defaultH: 80 },
    // PPM-specific costing elements
    { id: 'ppm-asset-table', label: 'Asset Summary', icon: '📋', defaultW: 720, defaultH: 120 },
    { id: 'ppm-pricing-table', label: 'Pricing Table', icon: '💰', defaultW: 720, defaultH: 200 },
    { id: 'ppm-callout-rates', label: 'Callout Rates', icon: '🚑', defaultW: 720, defaultH: 140 },
    { id: 'ppm-scope', label: 'Service Scope', icon: '🔧', defaultW: 720, defaultH: 120 },
    { id: 'ppm-terms', label: 'Terms & Conditions', icon: '📄', defaultW: 720, defaultH: 160 },
    { id: 'ppm-acceptance', label: 'Acceptance Form', icon: '✍️', defaultW: 720, defaultH: 180 },
    { id: 'ppm-renewal-banner', label: 'Renewal Banner', icon: '🔁', defaultW: 720, defaultH: 60 },
    { id: 'ppm-valid-until', label: 'Validity Block', icon: '📅', defaultW: 300, defaultH: 50 },
    { id: 'page-break', label: 'Page Break', icon: '📃', defaultW: 720, defaultH: 4 },
    { id: 'page-number', label: 'Page Number', icon: '🔢', defaultW: 200, defaultH: 20 },
    { id: 'signature', label: 'Signature', icon: '✒️', defaultW: 300, defaultH: 80 },
  ],
  showAreas: true,
  showVariables: true,
  outputType: 'pdf',
};

// ─── EMAIL MODE ───
export const EMAIL_MODE = {
  id: 'email',
  label: 'Email Template',
  canvasSizes: {
    email: { width: 600, height: 800, label: 'Email (600px)' },
  },
  defaultSize: 'email',
  components: [
    { id: 'header', label: 'Header', icon: '🏢', defaultW: 600, defaultH: 80 },
    { id: 'heading', label: 'Heading', icon: '📌', defaultW: 560, defaultH: 40 },
    { id: 'text', label: 'Text Block', icon: '📝', defaultW: 560, defaultH: 60 },
    { id: 'image', label: 'Image', icon: '🖼️', defaultW: 560, defaultH: 150 },
    { id: 'button', label: 'Button', icon: '🔘', defaultW: 200, defaultH: 44 },
    { id: 'divider', label: 'Divider', icon: '➖', defaultW: 560, defaultH: 10 },
    { id: 'social', label: 'Social Links', icon: '🔗', defaultW: 400, defaultH: 40 },
    { id: 'footer', label: 'Footer', icon: '📋', defaultW: 600, defaultH: 60 },
    { id: 'spacer', label: 'Spacer', icon: '⬜', defaultW: 560, defaultH: 20 },
    { id: '2col', label: '2 Columns', icon: '▥', defaultW: 560, defaultH: 80 },
  ],
  showAreas: false,
  showVariables: false,
  outputType: 'email', // table-based HTML
};

// ─── MOBILE MODE ───
export const MOBILE_MODE = {
  id: 'mobile',
  label: 'Mobile Form',
  canvasSizes: {
    phone: { width: 375, height: 667, label: 'iPhone SE' },
    phone_plus: { width: 414, height: 736, label: 'iPhone 8 Plus' },
    phone_x: { width: 390, height: 844, label: 'iPhone 14' },
    android: { width: 360, height: 640, label: 'Android' },
  },
  defaultSize: 'phone_x',
  components: [
    { id: 'header', label: 'Header', icon: '🏢', defaultW: 390, defaultH: 60 },
    { id: 'text', label: 'Text', icon: '📝', defaultW: 350, defaultH: 50 },
    { id: 'input', label: 'Input Field', icon: '✏️', defaultW: 350, defaultH: 44 },
    { id: 'textarea', label: 'Text Area', icon: '📄', defaultW: 350, defaultH: 80 },
    { id: 'select', label: 'Dropdown', icon: '📋', defaultW: 350, defaultH: 44 },
    { id: 'checkbox', label: 'Checkbox', icon: '☑️', defaultW: 350, defaultH: 30 },
    { id: 'image', label: 'Image', icon: '🖼️', defaultW: 350, defaultH: 120 },
    { id: 'button', label: 'Button', icon: '🔘', defaultW: 350, defaultH: 44 },
    { id: 'divider', label: 'Divider', icon: '➖', defaultW: 350, defaultH: 10 },
    { id: 'spacer', label: 'Spacer', icon: '⬜', defaultW: 350, defaultH: 16 },
  ],
  showAreas: true,
  showVariables: false,
  outputType: 'mobile', // React Native JSON layout
};

// ─── Default content for email component types ───
export const getEmailDefaultContent = (type) => {
  switch (type) {
    case 'header':
      return { text: BRAND.companyName, bgColor: BRAND.primaryColor, textColor: '#ffffff', fontSize: 22, logoUrl: '' };
    case 'heading':
      return { text: 'Your Heading Here', color: BRAND.primaryColor, fontSize: 20, align: 'center' };
    case 'text':
      return { text: 'Write your email content here. Click to edit any text directly on the canvas.', color: '#333333', fontSize: 14, lineHeight: 1.6, align: 'left' };
    case 'image':
      return { src: '', alt: 'Image', linkUrl: '' };
    case 'button':
      return { text: 'Click Here', url: '#', bgColor: BRAND.accentColor, textColor: '#ffffff', fontSize: 14, align: 'center', borderRadius: 4 };
    case 'divider':
      return { color: '#dddddd', thickness: 1 };
    case 'social':
      return { links: [{ platform: 'website', url: `https://${BRAND.website}` }, { platform: 'facebook', url: '' }, { platform: 'linkedin', url: '' }], color: BRAND.primaryColor };
    case 'footer':
      return { company: BRAND.companyName, phone: BRAND.phone, email: BRAND.email, website: BRAND.website, address: '', bgColor: BRAND.primaryColor, textColor: '#ffffff', fontSize: 12 };
    case 'spacer':
      return { height: 20 };
    case '2col':
      return { left: { text: 'Left column content', color: '#333333', fontSize: 14 }, right: { text: 'Right column content', color: '#333333', fontSize: 14 }, gap: 20 };
    default:
      return {};
  }
};

// ─── Default content for mobile component types ───
export const getMobileDefaultContent = (type) => {
  switch (type) {
    case 'header':
      return { text: 'Form Title', bgColor: BRAND.primaryColor, textColor: '#ffffff', fontSize: 18 };
    case 'text':
      return { text: 'Information text here.', color: '#333333', fontSize: 14 };
    case 'input':
      return { label: 'Field Label', placeholder: 'Enter value...', inputType: 'text', required: false };
    case 'textarea':
      return { label: 'Details', placeholder: 'Enter details...', required: false };
    case 'select':
      return { label: 'Select option', options: ['Option 1', 'Option 2', 'Option 3'], required: false };
    case 'checkbox':
      return { label: 'I agree to the terms', checked: false };
    case 'image':
      return { src: '', alt: 'Image' };
    case 'button':
      return { text: 'Submit', bgColor: BRAND.primaryColor, textColor: '#ffffff', action: 'submit' };
    case 'divider':
      return { color: '#e5e7eb', thickness: 1 };
    case 'spacer':
      return { height: 16 };
    default:
      return {};
  }
};

// ─── Email Zone Definitions ───
// Zones let AI and automation fill content by name instead of pixel coordinates.
// The renderer calculates positions automatically from zone order.

export const EMAIL_ZONES = {
  'brand-header': {
    label: 'Brand Header',
    description: 'Company name/logo with accent bar — navy background',
    minHeight: 84,
    padding: { top: 0, bottom: 0 },
    columns: [{ id: 'full', width: '100%' }],
    bgHint: '#112573',
    fixed: true,  // AI cannot remove this zone
  },
  'headline': {
    label: 'Headline',
    description: 'Main heading — first thing reader sees',
    minHeight: 40,
    padding: { top: 10, bottom: 5 },
    columns: [{ id: 'full', width: '100%' }],
    required: true,
  },
  'body-intro': {
    label: 'Introduction',
    description: 'Opening paragraph or lead text',
    minHeight: 60,
    padding: { top: 5, bottom: 10 },
    columns: [{ id: 'full', width: '100%' }],
  },
  'featured-article': {
    label: 'Featured Article',
    description: 'Main story or article — required for newsletters',
    minHeight: 100,
    padding: { top: 5, bottom: 5 },
    columns: [
      { id: 'left', width: '48%' },
      { id: 'right', width: '48%' },
    ],
    required: true,
  },
  'offer': {
    label: 'Offer / Promotion',
    description: 'Special offer, deal, or promotion — required for newsletters',
    minHeight: 80,
    padding: { top: 5, bottom: 5 },
    columns: [{ id: 'left', width: '48%' }, { id: 'right', width: '48%' }],
    required: true,
    rowGroup: 'offer-tip',  // shares row with quick-tip
  },
  'quick-tip': {
    label: 'Quick Tip',
    description: 'Weekly technical tip or advice — builds expertise and trust',
    minHeight: 60,
    padding: { top: 5, bottom: 10 },
    columns: [{ id: 'left', width: '48%' }, { id: 'right', width: '48%' }],
    rowGroup: 'offer-tip',  // shares row with offer
  },
  'cta': {
    label: 'Call to Action',
    description: 'Primary action button',
    minHeight: 44,
    padding: { top: 10, bottom: 10 },
    columns: [{ id: 'full', width: '100%' }],
  },
  'social-links': {
    label: 'Social Links',
    description: 'Website and social media links',
    minHeight: 40,
    padding: { top: 10, bottom: 5 },
    columns: [{ id: 'full', width: '100%' }],
  },
  'brand-footer': {
    label: 'Brand Footer',
    description: 'Company contact details — navy background',
    minHeight: 60,
    padding: { top: 0, bottom: 0 },
    columns: [{ id: 'full', width: '100%' }],
    bgHint: '#112573',
    fixed: true,
  },
};

export const EMAIL_ZONE_ORDER = [
  'brand-header', 'headline', 'body-intro',
  'featured-article', 'offer', 'quick-tip',
  'cta', 'social-links', 'brand-footer',
];

export const EMAIL_PAGE_WIDTH = 600;
export const EMAIL_MARGIN = 20;
export const EMAIL_CONTENT_WIDTH = 560;

/**
 * Calculate absolute x/y positions from zone-based email components.
 * Components with a `zone` field get positioned automatically.
 * Components without a zone fall through with their original x/y.
 */
export function calculateEmailZonePositions(components) {
  const result = [];
  const unzoned = [];
  let currentY = 0;

  // Group components by zone
  const zoneGroups = {};
  for (const comp of components) {
    const zone = comp.zone || 'unzoned';
    if (zone === 'unzoned') {
      unzoned.push(comp);
    } else {
      if (!zoneGroups[zone]) zoneGroups[zone] = [];
      zoneGroups[zone].push(comp);
    }
  }

  // Process zones in order
  const processedRowGroups = new Set();
  for (const zoneName of EMAIL_ZONE_ORDER) {
    const zoneComps = zoneGroups[zoneName];
    if (!zoneComps || zoneComps.length === 0) continue;

    const zoneConfig = EMAIL_ZONES[zoneName];
    const rowGroup = zoneConfig.rowGroup;

    // If this zone is part of a rowGroup already processed, skip it
    if (rowGroup && processedRowGroups.has(rowGroup)) continue;

    // Add zone separator before zone
    if (zoneName === 'brand-header') {
      // handled after header zone below
    } else if (zoneName === 'offer' || (rowGroup === 'offer-tip')) {
      currentY += 4;
      result.push({
        id: `zone-sep-offer-tip`,
        type: 'divider',
        x: EMAIL_MARGIN,
        y: currentY,
        width: EMAIL_CONTENT_WIDTH,
        height: 2,
        contentObj: { color: '#ff8900', thickness: 2 },
      });
      currentY += 6;
    } else if (zoneName !== 'brand-footer') {
      currentY += 4;
      result.push({
        id: `zone-sep-${zoneName}`,
        type: 'divider',
        x: EMAIL_MARGIN,
        y: currentY,
        width: EMAIL_CONTENT_WIDTH,
        height: 1,
        contentObj: { color: '#e2e8f0', thickness: 1 },
      });
      currentY += 4;
    }

    currentY += (zoneConfig.padding?.top || 0);

    if (rowGroup) {
      // Two zones side-by-side in a rowGroup
      processedRowGroups.add(rowGroup);
      const leftZoneName = zoneName; // first in order = left
      const rightZoneName = EMAIL_ZONE_ORDER.find(z => EMAIL_ZONES[z]?.rowGroup === rowGroup && z !== zoneName);
      const leftComps = zoneGroups[leftZoneName] || [];
      const rightComps = rightZoneName ? (zoneGroups[rightZoneName] || []) : [];

      const leftWidth = Math.floor(EMAIL_CONTENT_WIDTH * 0.48);
      const rightWidth = EMAIL_CONTENT_WIDTH - leftWidth - 20;
      let leftY = currentY;
      let rightY = currentY;

      for (const comp of leftComps) {
        const c = comp.contentObj || comp.content || {};
        const calculated = { ...comp, x: EMAIL_MARGIN, y: leftY, width: leftWidth, height: comp.height || c.height || 40 };
        result.push(calculated);
        leftY += calculated.height + 4;
      }
      for (const comp of rightComps) {
        const c = comp.contentObj || comp.content || {};
        const calculated = { ...comp, x: EMAIL_MARGIN + leftWidth + 20, y: rightY, width: rightWidth, height: comp.height || c.height || 40 };
        result.push(calculated);
        rightY += calculated.height + 4;
      }

      currentY = Math.max(leftY, rightY);
      currentY += (zoneConfig.padding?.bottom || 0);
      if (rightZoneName) currentY += (EMAIL_ZONES[rightZoneName]?.padding?.bottom || 0);
    } else {
      // Single zone — full width
      const cols = zoneConfig.columns || [{ id: 'full', width: '100%' }];
      const isFullWidth = cols.length === 1 && cols[0].id === 'full';

      if (isFullWidth) {
        let yPos = currentY;
        for (const comp of zoneComps) {
          const c = comp.contentObj || comp.content || {};
          const calculated = {
            ...comp,
            x: EMAIL_MARGIN,
            y: yPos,
            width: EMAIL_CONTENT_WIDTH,
            height: comp.height || c.height || (comp.type === 'header' || comp.type === 'footer' ? 80 : comp.type === '2col' ? 80 : 40),
          };
          result.push(calculated);
          yPos += calculated.height + 4;
        }
        currentY = yPos;
      } else {
        // Two-column within single zone
        const leftWidth = Math.floor(EMAIL_CONTENT_WIDTH * 0.48);
        const rightWidth = EMAIL_CONTENT_WIDTH - leftWidth - 20;
        const leftComps = zoneComps.filter(c => (c.column || 'left') === 'left');
        const rightComps = zoneComps.filter(c => c.column === 'right');
        let leftY = currentY;
        let rightY = currentY;

        for (const comp of leftComps) {
          const c = comp.contentObj || comp.content || {};
          const calculated = { ...comp, x: EMAIL_MARGIN, y: leftY, width: leftWidth, height: comp.height || c.height || 40 };
          result.push(calculated);
          leftY += calculated.height + 4;
        }
        for (const comp of rightComps) {
          const c = comp.contentObj || comp.content || {};
          const calculated = { ...comp, x: EMAIL_MARGIN + leftWidth + 20, y: rightY, width: rightWidth, height: comp.height || c.height || 40 };
          result.push(calculated);
          rightY += calculated.height + 4;
        }
        currentY = Math.max(leftY, rightY);
      }
      currentY += (zoneConfig.padding?.bottom || 0);
    }

    // Add brand-header accent line
    if (zoneName === 'brand-header') {
      result.push({
        id: `zone-accent-brand-header`,
        type: 'divider',
        x: 0,
        y: currentY,
        width: EMAIL_PAGE_WIDTH,
        height: 4,
        contentObj: { color: '#ff8900', thickness: 4 },
      });
      currentY += 8;
    }
  }

  // Append unzoned components
  for (const comp of unzoned) {
    result.push(comp);
  }

  return result;
}

// ─── Email HTML output (table-based for email clients) ───
export function componentsToEmailHtml(components, templateName) {
  // Resolve zone-based components to absolute positions
  const hasZoneLayout = components.some(c => c.zone);
  const resolved = hasZoneLayout ? calculateEmailZonePositions(components) : components;
  const sorted = [...resolved].sort((a, b) => a.y - b.y);

  let html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>${templateName}</title>
</head>
<body style="margin:0;padding:0;background-color:#f7f7f7;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f7f7f7;">
<tr><td align="center" style="padding:20px 0;">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;">\n`;

  for (let i = 0; i < sorted.length; i++) {
    const comp = sorted[i];
    const c = comp.contentObj || comp.content || {};

    // Detect side-by-side: if next component has same y and different x
    const nextComp = sorted[i + 1];
    const isSideBySide = nextComp && Math.abs(comp.y - nextComp.y) < 3 && Math.abs(comp.x - nextComp.x) > 50;

    if (isSideBySide) {
      // Render both in a 2-column row
      const leftComp = comp.x < nextComp.x ? comp : nextComp;
      const rightComp = comp.x < nextComp.x ? nextComp : comp;
      const leftC = leftComp.contentObj || leftComp.content || {};
      const rightC = rightComp.contentObj || rightComp.content || {};

      html += `<tr><td style="padding:10px 20px;"><table width="100%" cellpadding="0" cellspacing="0" border="0"><tr>\n`;
      html += `<td width="48%" style="vertical-align:top;padding-right:10px;font-family:Arial,sans-serif;">`;
      // Render left component inline
      if (leftComp.type === 'text') html += `<div style="color:${leftC.color || '#333'};font-size:${leftC.fontSize || 14}px;line-height:${leftC.lineHeight || 1.5};text-align:${leftC.align || 'left'};">${(leftC.text || '').replace(/\n/g, '<br/>')}</div>`;
      else if (leftComp.type === 'heading') html += `<div style="color:${leftC.color || '#333'};font-size:${leftC.fontSize || 18}px;font-weight:bold;text-align:${leftC.align || 'left'};">${leftC.text || ''}</div>`;
      else if (leftComp.type === 'button') html += `<div style="text-align:center;"><a href="${leftC.linkUrl || '#'}" style="display:inline-block;background-color:${leftC.bgColor || '#e53e3e'};color:${leftC.textColor || '#fff'};padding:10px 24px;border-radius:4px;text-decoration:none;font-weight:bold;font-size:${leftC.fontSize || 16}px;">${leftC.text || 'Click Here'}</a></div>`;
      else if (leftComp.type === 'image') html += `<img src="${leftC.src || leftC.url || ''}" alt="${leftC.alt || ''}" style="max-width:100%;height:auto;" />`;
      else html += `<div style="color:#333;font-size:14px;">${leftC.text || ''}</div>`;
      html += `</td>`;
      html += `<td width="4%" style="font-size:0;">&nbsp;</td>`;
      html += `<td width="48%" style="vertical-align:top;padding-left:10px;font-family:Arial,sans-serif;">`;
      // Render right component inline
      if (rightComp.type === 'text') html += `<div style="color:${rightC.color || '#333'};font-size:${rightC.fontSize || 14}px;line-height:${rightC.lineHeight || 1.5};text-align:${rightC.align || 'left'};">${(rightC.text || '').replace(/\n/g, '<br/>')}</div>`;
      else if (rightComp.type === 'heading') html += `<div style="color:${rightC.color || '#333'};font-size:${rightC.fontSize || 18}px;font-weight:bold;text-align:${rightC.align || 'left'};">${rightC.text || ''}</div>`;
      else if (rightComp.type === 'button') html += `<div style="text-align:center;"><a href="${rightC.linkUrl || '#'}" style="display:inline-block;background-color:${rightC.bgColor || '#e53e3e'};color:${rightC.textColor || '#fff'};padding:10px 24px;border-radius:4px;text-decoration:none;font-weight:bold;font-size:${rightC.fontSize || 16}px;">${rightC.text || 'Click Here'}</a></div>`;
      else if (rightComp.type === 'image') html += `<img src="${rightC.src || rightC.url || ''}" alt="${rightC.alt || ''}" style="max-width:100%;height:auto;" />`;
      else html += `<div style="color:#333;font-size:14px;">${rightC.text || ''}</div>`;
      html += `</td>`;
      html += `</tr></table></td></tr>\n`;
      i++; // skip next component
      continue;
    }
    switch (comp.type) {
      case 'header': {
        const hasText = c.companyText || c.text;
        if (c.logoUrl && hasText) {
          // Logo left, text right
          html += `<tr><td style="background-color:${c.bgColor || '#1a365d'};color:${c.textColor || '#fff'};padding:20px;font-family:Arial,sans-serif;">
            <table width="100%" cellpadding="0" cellspacing="0"><tr>
              <td style="vertical-align:middle;text-align:left;"><img src="${c.logoUrl}" alt="Logo" style="max-height:40px;height:auto;" /></td>
              <td style="vertical-align:middle;text-align:right;font-size:${c.fontSize || 22}px;font-weight:bold;color:${c.textColor || '#fff'};">${c.companyText || c.text}</td>
            </tr></table>
          </td></tr>\n`;
        } else if (c.logoUrl) {
          // Logo only
          html += `<tr><td style="background-color:${c.bgColor || '#1a365d'};padding:20px;text-align:left;font-family:Arial,sans-serif;">
            <img src="${c.logoUrl}" alt="Logo" style="max-height:40px;height:auto;" />
          </td></tr>\n`;
        } else {
          // Text only
          html += `<tr><td style="background-color:${c.bgColor || '#1a365d'};color:${c.textColor || '#fff'};padding:20px;text-align:center;font-family:Arial,sans-serif;font-size:${c.fontSize || 22}px;font-weight:bold;">${c.companyText || c.text || 'Company Name'}</td></tr>\n`;
        }
        break;
      }
      case 'heading':
        html += `<tr><td style="color:${c.color || '#333'};font-size:${c.fontSize || 18}px;font-weight:bold;text-align:${c.align || 'left'};font-family:Arial,sans-serif;padding:10px 20px;">${c.text || ''}</td></tr>\n`;
        break;
      case 'text':
        html += `<tr><td style="color:${c.color || '#333'};font-size:${c.fontSize || 14}px;line-height:${c.lineHeight || 1.5};text-align:${c.align || 'left'};font-family:Arial,sans-serif;padding:10px 20px;">${(c.text || '').replace(/\n/g, '<br/>')}</td></tr>\n`;
        break;
      case 'image':
        if (c.src) {
          html += `<tr><td style="padding:10px 20px;text-align:center;">`;
          if (c.linkUrl) html += `<a href="${c.linkUrl}">`;
          html += `<img src="${c.src}" alt="${c.alt || ''}" style="max-width:100%;height:auto;" />`;
          if (c.linkUrl) html += `</a>`;
          html += `</td></tr>\n`;
        }
        break;
      case 'button':
        html += `<tr><td style="padding:10px 20px;text-align:${c.align || 'center'};">
          <a href="${c.url || '#'}" style="display:inline-block;background-color:${c.bgColor || '#e53e3e'};color:${c.textColor || '#fff'};padding:12px 28px;border-radius:${c.borderRadius || 4}px;font-size:${c.fontSize || 14}px;font-weight:bold;text-decoration:none;font-family:Arial,sans-serif;">${c.text || 'Click Here'}</a>
        </td></tr>\n`;
        break;
      case 'divider':
        html += `<tr><td style="padding:10px 20px;"><hr style="border:none;border-top:${c.thickness || 1}px solid ${c.color || '#ddd'};margin:0;" /></td></tr>\n`;
        break;
      case 'social':
        html += `<tr><td style="padding:10px 20px;text-align:center;font-family:Arial,sans-serif;font-size:12px;">`;
        (c.links || []).filter(l => l.url).forEach(l => {
          html += `<a href="${l.url}" style="display:inline-block;margin:0 8px;color:${c.color || '#1a365d'};text-decoration:none;">${l.platform}</a>`;
        });
        html += `</td></tr>\n`;
        break;
      case 'footer':
        html += `<tr><td style="background-color:${c.bgColor || '#1a365d'};color:${c.textColor || '#fff'};padding:20px;text-align:center;font-family:Arial,sans-serif;">`;
        html += `<div style="font-size:${(c.fontSize || 12) + 2}px;font-weight:bold;margin-bottom:8px;">${c.company || ''}</div>`;
        html += `<div style="font-size:${c.fontSize || 11}px;opacity:0.85;">`;
        if (c.phone) html += `<span>📞 ${c.phone}</span>`;
        if (c.phone && (c.email || c.website)) html += `&nbsp;&nbsp;&nbsp;`;
        if (c.email) html += `<span>✉️ <a href="mailto:${c.email}" style="color:${c.textColor || '#fff'};text-decoration:underline;">${c.email}</a></span>`;
        if (c.email && c.website) html += `&nbsp;&nbsp;&nbsp;`;
        if (c.website) html += `<span>🌐 <a href="${c.website.startsWith('http') ? c.website : 'https://' + c.website}" style="color:${c.textColor || '#fff'};text-decoration:underline;">${c.website}</a></span>`;
        html += `</div>`;
        if (c.address) html += `<div style="font-size:10px;opacity:0.7;margin-top:6px;">${c.address}</div>`;
        html += `</td></tr>\n`;
        break;
      case 'spacer':
        html += `<tr><td style="height:${c.height || 20}px;font-size:0;line-height:0;">&nbsp;</td></tr>\n`;
        break;
      case '2col':
        html += `<tr><td style="padding:10px 20px;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
          <td width="48%" style="color:${c.left?.color || '#333'};font-size:${c.left?.fontSize || 14}px;font-family:Arial,sans-serif;vertical-align:top;">${(c.left?.text || '').replace(/\n/g, '<br/>')}</td>
          <td width="4%"></td>
          <td width="48%" style="color:${c.right?.color || '#333'};font-size:${c.right?.fontSize || 14}px;font-family:Arial,sans-serif;vertical-align:top;">${(c.right?.text || '').replace(/\n/g, '<br/>')}</td>
          </tr></table>
        </td></tr>\n`;
        break;
    }
  }

  html += `</table>
</td></tr>
</table>
</body>
</html>`;
  return html;
}

// ─── Mobile JSON output (for React Native rendering) ───
export function componentsToMobileLayout(components) {
  const sorted = [...components].sort((a, b) => a.y - b.y);
  const elements = sorted.map(comp => {
    const c = comp.contentObj || comp.content || {};
    switch (comp.type) {
      case 'header':
        return { type: 'header', props: { text: c.text, bgColor: c.bgColor, textColor: c.textColor, fontSize: c.fontSize } };
      case 'text':
        return { type: 'text', props: { text: c.text, color: c.color, fontSize: c.fontSize } };
      case 'input':
        return { type: 'input', props: { label: c.label, placeholder: c.placeholder, inputType: c.inputType, required: c.required } };
      case 'textarea':
        return { type: 'textarea', props: { label: c.label, placeholder: c.placeholder, required: c.required } };
      case 'select':
        return { type: 'select', props: { label: c.label, options: c.options, required: c.required } };
      case 'checkbox':
        return { type: 'checkbox', props: { label: c.label, checked: c.checked } };
      case 'image':
        return { type: 'image', props: { src: c.src, alt: c.alt } };
      case 'button':
        return { type: 'button', props: { text: c.text, bgColor: c.bgColor, textColor: c.textColor, action: c.action } };
      case 'divider':
        return { type: 'divider', props: { color: c.color, thickness: c.thickness } };
      case 'spacer':
        return { type: 'spacer', props: { height: c.height } };
      default:
        return { type: comp.type, props: c };
    }
  });

  return {
    version: 1,
    layout: 'scroll',
    elements,
  };
}
